/**
 * /app/onboarding — hosts the post-tutorial questionnaire (OnboardingFlow) and wires it to
 * data + navigation. Presentational flow lives in components/onboarding/OnboardingFlow.
 * See specs/onboarding-questionnaire/spec.md.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'expo-router';
import { OnboardingFlow } from '../../components/onboarding/OnboardingFlow/OnboardingFlow';
import type { OnboardingLeague } from '../../components/onboarding/OnboardingFlow/steps/LeaguesStep';
import { useApiClient } from '../../helpers/apiClient.helper';
import { useFavoriteLeagues } from '../../src/create/hooks/useFavoriteLeagues';
import { checkUsernameAvailable, setUsername, type ApiClientFn } from '../../src/onboarding/api/usernameApi';
import { markQuestionnaireSeen } from '../../src/onboarding/helpers/questionnaireSeenStorage.helper';
import { useFirstChallengeTourStore } from '../../src/onboarding/stores/firstChallengeTourStore';
import { useAgeVerification } from '../../src/ageGate/hooks/useAgeVerification';
import { API_ENDPOINTS } from '../../constants';

/** Compact badge text for a league name: multi-word → initials, single word → first two letters. */
function leagueInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return words.slice(0, 3).map((w) => w[0]).join('').toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function OnboardingRoute() {
  const router = useRouter();
  const apiClient = useApiClient() as ApiClientFn;
  const { favoriteLeagueIds, toggleFavorite } = useFavoriteLeagues();
  const age = useAgeVerification();

  const [leagues, setLeagues] = useState<OnboardingLeague[]>([]);
  const [leaguesLoading, setLeaguesLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiClient(API_ENDPOINTS.matches.leagues);
        const rows = (res?.data as { data?: Array<{ id?: unknown; name?: unknown; country?: unknown }> })?.data ?? [];
        const mapped: OnboardingLeague[] = rows
          .filter((r) => typeof r.id === 'string' && typeof r.name === 'string')
          .map((r) => ({
            id: r.id as string,
            name: r.name as string,
            initials: leagueInitials(r.name as string),
            country: typeof r.country === 'string' ? r.country : undefined,
          }));
        if (active) setLeagues(mapped);
      } catch {
        if (active) setLeagues([]);
      } finally {
        if (active) setLeaguesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [apiClient]);

  const checkUsername = useCallback(
    (name: string) => checkUsernameAvailable(name, apiClient),
    [apiClient],
  );
  const saveUsername = useCallback(
    (name: string) => setUsername(name, apiClient),
    [apiClient],
  );

  const onFinish = useCallback(() => {
    void markQuestionnaireSeen();
    // Hand off to the Arena and queue the first-challenge guided tour (starts on Arena focus).
    useFirstChallengeTourStore.getState().setPendingStart(true);
    router.replace('/app/arena' as `/${string}`);
  }, [router]);

  const onExit = useCallback(() => {
    router.replace('/app/tutorial' as `/${string}`);
  }, [router]);

  const selectedLeagueIds = useMemo(() => favoriteLeagueIds, [favoriteLeagueIds]);

  return (
    <OnboardingFlow
      checkUsername={checkUsername}
      saveUsername={saveUsername}
      age={age}
      leagues={leagues}
      leaguesLoading={leaguesLoading}
      selectedLeagueIds={selectedLeagueIds}
      onToggleLeague={toggleFavorite}
      onFinish={onFinish}
      onExit={onExit}
    />
  );
}
